export type AdmissionStatus = "waiting" | "in_progress" | "oriented" | "pending_billing";

/** Triage 3 niveaux — vert / orange / rouge */
export type TriageLevel = "green" | "yellow" | "red";

export type AdmissionChannel = "admission" | "emergency";

/** Mode d'arrivée aux urgences. */
export type EmergencyArrivalMode = "walk_in" | "ambulance" | "referral" | "police" | "other";

export interface Admission {
  id: string;
  patientId: string;
  channel: AdmissionChannel;
  status: AdmissionStatus;
  triageLevel: TriageLevel | null;
  motif: string;
  /** Services orientés (libellés séparés par virgule si plusieurs). */
  destination: string;
  destinations: string[];
  createdAt: string;
  updatedAt: string;
  orientedAt: string | null;
  /** Nom saisi à l'accueil si visiteur non enregistré (dossier comptabilité). */
  walkInName: string;
  billingDocumentId: string | null;
  /** Services bloqués faute de couverture / paiement (transmis comptabilité). */
  blockedDestinations: string[];
  /** Urgences — mode d'arrivée (null hors channel emergency). */
  arrivalMode: EmergencyArrivalMode | null;
  /** Urgences — score de Glasgow 3–15. */
  glasgowScore: number | null;
  /** Urgences — allergies / antécédents signalés au triage. */
  allergiesNote: string;
  /** Urgences — notes infirmier / trieur. */
  triageNotes: string;
  /** Urgences — horodatage du triage validé. */
  triagedAt: string | null;
}

export interface AdmissionInput {
  patientId: string;
  channel: AdmissionChannel;
  motif: string;
  triageLevel?: TriageLevel | null;
  walkInName?: string;
  arrivalMode?: EmergencyArrivalMode;
  glasgowScore?: number | null;
  allergiesNote?: string;
  triageNotes?: string;
}

export interface EmergencyTriagePatch {
  triageLevel?: TriageLevel;
  arrivalMode?: EmergencyArrivalMode;
  glasgowScore?: number | null;
  allergiesNote?: string;
  triageNotes?: string;
}

export const ADMISSION_STATUS_LABELS: Record<AdmissionStatus, string> = {
  waiting: "En attente",
  in_progress: "En cours",
  oriented: "Orienté",
  pending_billing: "Comptabilité",
};

export const TRIAGE_LABELS: Record<TriageLevel, string> = {
  green: "Niveau 3 — Stable",
  yellow: "Niveau 2 — Urgent",
  red: "Niveau 1 — Critique",
};

export const TRIAGE_SHORT: Record<TriageLevel, string> = {
  green: "P3",
  yellow: "P2",
  red: "P1",
};

export const DESTINATION_OPTIONS = [
  "Consultation externe",
  "Médecine générale",
  "Consultation résultats",
  "Pédiatrie",
  "Chirurgie",
  "Maternité",
  "Hospitalisation",
  "Soins infirmiers — constantes",
  "Laboratoire",
  "Imagerie",
  "Pharmacie",
  "Sortie",
] as const;

export const EMERGENCY_ARRIVAL_LABELS: Record<EmergencyArrivalMode, string> = {
  walk_in: "À pied",
  ambulance: "Ambulance / SMUR",
  referral: "Transfert / orientation",
  police: "Forces de l'ordre",
  other: "Autre",
};

export const EMERGENCY_EXTRA_DESTINATIONS = ["Laboratoire", "Imagerie", "Box urgences"] as const;
